import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiCreditCard, FiClock, FiInfo, FiSmartphone, FiBriefcase, FiDollarSign, FiUpload, FiTrash2, FiFile, FiAlertCircle } from 'react-icons/fi';
import { portalAPI } from '../../services/api';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('fr-FR')} XOF`;

// Icons for payment methods
const METHOD_ICONS = {
  'orange-money': FiSmartphone,
  'moov-money': FiSmartphone,
  wave: FiSmartphone,
  'virement-bancaire': FiBriefcase,
  cheque: FiDollarSign,
  'carte-bancaire': FiCreditCard,
  especes: FiDollarSign,
};

const getMethodIcon = (slug) => METHOD_ICONS[slug] || FiCreditCard;

// ─── COMPOSANT UPLOAD REÇU UNIVERSEL ───
const ReceiptUpload = ({ receiptImage, setReceiptImage }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setReceiptImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setReceiptImage(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (receiptImage) {
    const isImage = receiptImage.type?.startsWith('image/');
    const previewUrl = isImage ? URL.createObjectURL(receiptImage) : null;

    return (
      <div style={{
        background: '#f0fdf4',
        border: '1px solid #86efac',
        borderRadius: 10,
        padding: 16,
        marginTop: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isImage && previewUrl ? (
            <img src={previewUrl} alt="Reçu" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />
          ) : (
            <div style={{
              width: 56, height: 56, background: '#fef3c7', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FiFile size={24} color="#d97706" />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {receiptImage.name}
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
              {formatFileSize(receiptImage.size)} · {receiptImage.type || 'Fichier'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setReceiptImage(null)}
            style={{
              background: '#fee2e2', color: '#ef4444', border: 'none',
              borderRadius: 6, padding: '6px 10px', fontSize: 12,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
            }}
          >
            <FiTrash2 size={14} /> Retirer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <label style={{ fontSize: 13, color: '#475569', fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
        <FiUpload size={14} />
        Reçu / Preuve de paiement <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span>
      </label>
      <div
        onClick={() => document.getElementById('receiptInput').click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${dragOver ? '#3b82f6' : '#cbd5e1'}`,
          borderRadius: 10,
          padding: '24px 16px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragOver ? '#eff6ff' : 'white',
          transition: 'all 0.2s'
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
        <div style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>Cliquez ou glissez un fichier ici</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Images (JPG, PNG) ou PDF — max 5 Mo</div>
      </div>
      <input
        id="receiptInput"
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

const PortalPaymentCheckoutPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [status, setStatus] = useState(() => searchParams.get('status') || 'pending');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedMethodDetails, setSelectedMethodDetails] = useState(null);
  const [paymentType, setPaymentType] = useState('full');
  const [installmentType, setInstallmentType] = useState('half');
  const [customInstallmentAmount, setCustomInstallmentAmount] = useState('');
  const [advanceMonths, setAdvanceMonths] = useState(1);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  
  // ─── REÇU UNIVERSEL (optionnel pour TOUTES les méthodes) ───
  const [receiptImage, setReceiptImage] = useState(null);

  // Manual payment form state (gardé pour les méthodes manuelles)
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualReference, setManualReference] = useState('');
  const [manualNotes, setManualNotes] = useState('');

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const { data } = await portalAPI.billing.getLeasePaymentById(id);
        setPayment(data);
        setCalculatedAmount(Number(data.amount) + Number(data.late_fee || 0));
        if (data.status === 'paid') {
          setStatus('success');
        }
      } catch (err) {
        alert('Erreur lors du chargement des détails du paiement');
        navigate('/portal/payments');
      } finally {
        setLoading(false);
      }
    };
    fetchPayment();
    
    const fetchMethods = async () => {
      try {
        const { data } = await portalAPI.billing.getAgencyPaymentMethods();
        const methods = Array.isArray(data) ? data : [];
        setPaymentMethods(methods);
        if (methods.length > 0) {
          const firstMethod = methods[0];
          const methodId = firstMethod.payment_method_id || firstMethod.id;
          setSelectedMethod(methodId);
          setSelectedMethodDetails(firstMethod);
          const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
          setShowManualForm(manualMethods.includes(firstMethod.slug));
        }
      } catch (e) {
        console.warn('Cannot load payment methods:', e);
      }
    };
    fetchMethods();
  }, [id, navigate]);

  useEffect(() => {
    if (status === 'initiated') {
      const checkIntentStatus = async () => {
        try {
          const { data: intents } = await portalAPI.payments.listMyIntents({ lease_payment_id: id });
          if (intents && intents.length > 0) {
            const latest = intents[0];
            if (latest.status === 'succeeded' || latest.status === 'validated' || latest.status === 'paid') {
              setStatus('success');
            } else if (latest.status === 'rejected') {
              setStatus('failed');
            }
          }
        } catch (e) {
          // silencieux
        }
      };
      checkIntentStatus();
    }
  }, [status, id]);

  useEffect(() => {
    if (!payment) return;
    const baseAmount = Number(payment.amount) + Number(payment.late_fee || 0);
    if (paymentType === 'full') {
      setCalculatedAmount(baseAmount);
    } else if (paymentType === 'installment') {
      if (installmentType === 'half') {
        setCalculatedAmount(baseAmount / 2);
      } else {
        const val = parseFloat(customInstallmentAmount) || 0;
        setCalculatedAmount(val);
      }
    } else if (paymentType === 'advance') {
      setCalculatedAmount(baseAmount * (advanceMonths + 1));
    }
  }, [paymentType, installmentType, customInstallmentAmount, advanceMonths, payment]);

  const handleMethodSelect = (method) => {
    const methodId = method.payment_method_id || method.id;
    setSelectedMethod(methodId);
    setSelectedMethodDetails(method);
    const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
    setShowManualForm(manualMethods.includes(method.slug));
  };

  const handleInitiatePayment = async () => {
    const baseAmount = Number(payment.amount) + Number(payment.late_fee || 0);
    
    if (paymentType === 'installment') {
      if (calculatedAmount <= 0 || calculatedAmount >= baseAmount) {
        alert(`Le montant de la tranche doit être supérieur à 0 et inférieur à ${formatCurrency(baseAmount)}`);
        return;
      }
    }

    setProcessing(true);

    try {
      const providerSlug = selectedMethodDetails?.slug || 'orange_money';
      const manualMethods = ['virement-bancaire', 'cheque', 'especes'];
      const isManual = manualMethods.includes(providerSlug);

      // ─── CONSTRUIRE LE FORMDATA AVEC REÇU OPTIONNEL ───
      const formData = new FormData();
      formData.append('payment_method_id', selectedMethod);
      formData.append('type', paymentType === 'full' ? 'single' : paymentType);
      
      if (paymentType === 'advance') {
        formData.append('advance_amount', calculatedAmount);
      } else if (paymentType === 'installment') {
        formData.append('installment_amount', calculatedAmount);
      }

      // ⬇️ AJOUT DU REÇU OPTIONNEL (pour TOUTES les méthodes)
      if (receiptImage) {
        formData.append('file', receiptImage);
      }

      if (isManual) {
        // ─── PAIEMENT MANUEL ───
        formData.append('reference', manualReference);
        formData.append('notes', manualNotes);
        formData.append('amount', calculatedAmount);

        await portalAPI.billing.submitManualPayment(id, formData);
      } else {
        // ─── PAIEMENT AUTOMATIQUE (OM, Wave, etc.) ───
        formData.append('provider', providerSlug);
        formData.append('lease_payment_id', id);

        await portalAPI.billing.checkoutLeasePayment(id, formData);
      }

      setStatus('initiated');
    } catch (err) {
      setStatus('failed');
      alert(err.response?.data?.error || "Erreur lors de la soumission du paiement");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!payment) return null;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 20px' }}>
      <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
        {status === 'success' ? (
          <div>
            <FiCheckCircle size={64} color="green" style={{ marginBottom: 20 }} />
            <h2>Paiement validé !</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '16px 0' }}>
              Votre paiement pour l'échéance du {new Date(payment.due_date).toLocaleDateString('fr-FR')} a été confirmé par l'agence.
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/portal/lease-payments')}>
              Retour aux contrats
            </button>
          </div>
        ) : status === 'initiated' ? (
          <div>
            <FiClock size={64} color="#f59e0b" style={{ marginBottom: 20 }} />
            <h2>Demande soumise avec succès</h2>
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: 8,
              padding: '16px 20px',
              margin: '24px 0',
              textAlign: 'left'
            }}>
              <p style={{ margin: 0, color: '#92400e' }}>
                <strong>Votre demande de paiement a été transmise à l'agence.</strong>
              </p>
              <p style={{ margin: '8px 0 0', color: '#92400e', fontSize: 14 }}>
                L'agence va vérifier et valider votre paiement sous peu. 
                Vous recevrez une notification dès que la validation sera effectuée.
              </p>
            </div>
            <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
              <div className="detail-row">
                <span className="detail-label">Contrat:</span>
                <span className="detail-value">{payment.contract_number}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Propriété:</span>
                <span className="detail-value">{payment.property_title}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date d'échéance:</span>
                <span className="detail-value">{new Date(payment.due_date).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Type de paiement:</span>
                <span className="detail-value">
                  {paymentType === 'full' ? 'Paiement complet' : 
                   paymentType === 'installment' ? `Paiement par tranche (${installmentType === 'half' ? 'Moitié' : 'Personnalisé'})` : 
                   `Paiement anticipé (${advanceMonths + 1} mois)`}
                </span>
              </div>
              <hr style={{ margin: '16px 0', borderTop: '1px solid var(--border-color)' }} />
              <div className="detail-row" style={{ fontSize: 18, fontWeight: 'bold' }}>
                <span className="detail-label">Total à payer:</span>
                <span className="detail-value" style={{ color: 'var(--primary-color)' }}>
                  {formatCurrency(calculatedAmount)}
                </span>
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/portal/lease-payments')}>
              Retour aux contrats
            </button>
          </div>
        ) : status === 'failed' ? (
          <div>
            <FiXCircle size={64} color="red" style={{ marginBottom: 20 }} />
            <h2>Échec du paiement</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '16px 0' }}>
              Une erreur est survenue lors du traitement de votre paiement.
            </p>
            <button className="btn btn-secondary" onClick={() => setStatus('pending')} style={{ marginRight: 12 }}>
              Réessayer
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/portal/lease-payments')}>
              Retour aux contrats
            </button>
          </div>
        ) : (
          <div>
            <FiCreditCard size={48} color="var(--primary-color)" style={{ marginBottom: 20 }} />
            <h2>Finaliser votre paiement</h2>
            
            <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
              <div className="detail-row">
                <span className="detail-label">Contrat:</span>
                <span className="detail-value">{payment.contract_number}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Propriété:</span>
                <span className="detail-value">{payment.property_title}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date d'échéance:</span>
                <span className="detail-value">{new Date(payment.due_date).toLocaleDateString('fr-FR')}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Loyer mensuel:</span>
                <span className="detail-value">{formatCurrency(payment.amount)}</span>
              </div>
              {Number(payment.late_fee) > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Pénalité de retard:</span>
                  <span className="detail-value" style={{ color: 'red' }}>{formatCurrency(payment.late_fee)}</span>
                </div>
              )}
            </div>

            {/* Type de paiement */}
            <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
              <h4 style={{ margin: '0 0 12px' }}>Type de paiement</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`btn ${paymentType === 'full' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setPaymentType('full')}
                  style={{ flex: 1, minWidth: 100 }}
                >
                  Paiement complet
                </button>
                <button
                  type="button"
                  className={`btn ${paymentType === 'installment' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setPaymentType('installment')}
                  style={{ flex: 1, minWidth: 100 }}
                >
                  Tranche
                </button>
                <button
                  type="button"
                  className={`btn ${paymentType === 'advance' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setPaymentType('advance')}
                  style={{ flex: 1, minWidth: 100 }}
                >
                  Payer en avance
                </button>
              </div>

              {paymentType === 'installment' && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                    Option de tranche :
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className={`btn ${installmentType === 'half' ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setInstallmentType('half')}
                      style={{ flex: 1 }}
                    >
                      Moitié (50%)
                    </button>
                    <button
                      type="button"
                      className={`btn ${installmentType === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setInstallmentType('custom')}
                      style={{ flex: 1 }}
                    >
                      Montant personnalisé
                    </button>
                  </div>
                  {installmentType === 'custom' && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        Montant de la tranche (XOF) :
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ marginTop: 6 }}
                        value={customInstallmentAmount}
                        onChange={(e) => setCustomInstallmentAmount(e.target.value)}
                        placeholder="ex: 50000"
                        min="1"
                        max={Number(payment.amount) + Number(payment.late_fee || 0) - 1}
                      />
                      <small style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                        Le montant doit être inférieur à {formatCurrency(Number(payment.amount) + Number(payment.late_fee || 0))}
                      </small>
                    </div>
                  )}
                </div>
              )}

              {paymentType === 'advance' && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--border-color)', paddingTop: 12 }}>
                  <label style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
                    Nombre de mois à payer en avance (en plus du mois courant) :
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1, 2, 3, 6].map(n => (
                      <button
                        key={n}
                        type="button"
                        className={`btn ${advanceMonths === n ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setAdvanceMonths(n)}
                        style={{ flex: 1 }}
                      >
                        {n} mois
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Méthode de paiement */}
            {paymentMethods.length > 0 ? (
              <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 12px' }}>Méthode de paiement</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {paymentMethods.map(method => {
                    const Icon = getMethodIcon(method.slug);
                    const methodId = method.payment_method_id || method.id;
                    const isSelected = selectedMethod === methodId;
                    return (
                      <button
                        key={method.id || methodId}
                        type="button"
                        className={`btn ${isSelected ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => handleMethodSelect(method)}
                        style={{ flex: 1, minWidth: 120, position: 'relative' }}
                        title={method.details?.instructions || method.description}
                      >
                        <Icon style={{ marginRight: 6 }} />
                        {method.name}
                      </button>
                    );
                  })}
                </div>
                
                {selectedMethodDetails && (
                  <div style={{ marginTop: 12, padding: 12, background: 'white', borderRadius: 6, border: '1px solid var(--border)' }}>
                    {selectedMethodDetails.details && Object.keys(selectedMethodDetails.details).length > 0 && (
                      <div style={{ fontSize: 13 }}>
                        {Object.entries(selectedMethodDetails.details).map(([key, value]) => (
                          <div key={key} style={{ marginBottom: 4 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}:</strong> {value}
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedMethodDetails.details?.instructions && (
                      <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
                        <FiInfo style={{ marginRight: 4 }} />
                        {selectedMethodDetails.details.instructions}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 16, borderRadius: 8, margin: '24px 0', textAlign: 'left', fontSize: 13 }}>
                <FiAlertCircle style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Chargement des méthodes de paiement...
              </div>
            )}

            {/* ─── FORMULAIRE MANUEL (uniquement si méthode manuelle) ─── */}
            {showManualForm && (
              <div style={{ background: 'var(--bg-secondary)', padding: 20, borderRadius: 8, margin: '24px 0', textAlign: 'left' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 14 }}>Détails du paiement manuel</h4>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13 }}>Référence du paiement</label>
                  <input
                    type="text"
                    className="form-control"
                    value={manualReference}
                    onChange={(e) => setManualReference(e.target.value)}
                    placeholder="ex: VIREMENT-123456"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 13 }}>Notes (optionnel)</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={manualNotes}
                    onChange={(e) => setManualNotes(e.target.value)}
                    placeholder="Ajoutez des informations supplémentaires..."
                  />
                </div>
              </div>
            )}

            {/* ─── UPLOAD REÇU UNIVERSEL (TOUTES les méthodes) ─── */}
            <div style={{ textAlign: 'left' }}>
              <ReceiptUpload receiptImage={receiptImage} setReceiptImage={setReceiptImage} />
            </div>

            <hr style={{ margin: '16px 0', borderTop: '1px solid var(--border-color)' }} />
            <div className="detail-row" style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 20 }}>
              <span className="detail-label">Total à payer:</span>
              <span className="detail-value" style={{ color: 'var(--primary-color)' }}>
                {formatCurrency(calculatedAmount)}
              </span>
            </div>

            <button 
              className="btn btn-primary" 
              style={{ width: '100%', justifyContent: 'center', fontSize: 16, padding: '12px 24px' }}
              onClick={handleInitiatePayment}
              disabled={processing || !selectedMethod}
            >
              <FiCreditCard style={{ marginRight: 8 }} />
              {processing ? 'Traitement en cours...' : showManualForm ? 'Soumettre le paiement' : 'Soumettre la demande de paiement'}
            </button>
            
            <button 
              className="btn btn-secondary" 
              style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
              onClick={() => navigate('/portal/lease-payments')}
              disabled={processing}
            >
              Annuler
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalPaymentCheckoutPage;